import { uploadAssetUrl } from './upload-asset-url';

function dedupePreserveOrder(urls: string[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const u of urls) {
    if (!u || seen.has(u)) continue;
    seen.add(u);
    out.push(u);
  }
  return out;
}

/**
 * Stored DB paths sometimes use legacy `lessons/.../slides/<slideId>/file` while files are written
 * to canonical `lessons/.../<slideId>/file` (see Backend/utils/uploadLayout writeLessonSlideImage).
 * When static misses the wrong path, the request falls through to JWT middleware → "Invalid Authorization"
 * on bare <img src> requests. Emit alternates in the same search order as the backend resolver.
 */
function lessonStoredRelativePathCandidates(rel: string): string[] {
  const n = rel.replace(/\\/g, '/').replace(/^\/+/, '');
  const m = n.match(/^(lessons\/u\d+_l\d+)\/slides\/([^/]+)\/(.+)$/i);
  if (m) {
    const root = m[1];
    const slideId = m[2];
    const rest = m[3];
    const canonical = `${root}/${slideId}/${rest}`;
    const base = rest.split('/').filter(Boolean).pop();
    const pool = base && !base.includes('..') ? `${root}/images/${base}` : null;
    return dedupePreserveOrder([canonical, n, ...(pool ? [pool] : [])]);
  }
  return [n];
}

/**
 * Ordered relative paths for a lesson IMAGE filename (basename only).
 * Matches Backend/utils/uploadLayout lessonImageSearchDirs resolution order.
 */
function lessonBasenameRelativePaths(userId: number, lessonId: number, lessonSlideId: string, filename: string): string[] {
  const key = `u${Number(userId)}_l${Number(lessonId)}`;
  const sid = String(lessonSlideId || '').trim();
  const f = String(filename || '').trim();
  if (!sid || !f || f.includes('..')) return [];
  return [`lessons/${key}/${sid}/${f}`, `lessons/${key}/slides/${sid}/${f}`, `lessons/${key}/images/${f}`];
}

/**
 * Full URLs to try for an IMAGE element's `content` (basename, relative, or absolute http).
 */
export function lessonImageContentUrlCandidates(
  userId: number,
  lessonId: number,
  lessonSlideId: string,
  content: string | null | undefined
): string[] {
  const raw = String(content ?? '')
    .trim()
    .replace(/^['"]+|['"]+$/g, '');
  if (!raw) return [];
  if (/^https?:\/\//i.test(raw)) return [raw];
  const norm = raw.replace(/\\/g, '/');
  if (norm.startsWith('lessons/')) {
    return dedupePreserveOrder(lessonStoredRelativePathCandidates(norm).map((p) => uploadAssetUrl(p)));
  }
  if (norm.startsWith('projects/')) {
    return [uploadAssetUrl(norm)];
  }
  if (norm.includes('/')) {
    return [uploadAssetUrl(norm)];
  }
  return lessonBasenameRelativePaths(userId, lessonId, lessonSlideId, raw).map((p) => uploadAssetUrl(p));
}

/** First candidate URL (for [src] binding). */
export function lessonImageContentPrimaryUrl(
  userId: number,
  lessonId: number,
  lessonSlideId: string,
  content: string | null | undefined
): string | null {
  const c = lessonImageContentUrlCandidates(userId, lessonId, lessonSlideId, content);
  return c.length ? c[0] : null;
}
